/**
 * API client for retrieving and updating an order's denial/query reason buckets.
 *
 * @module api/services/denialQueryBucketService
 */

import type { DenialQueryBucketSummaryResponse } from "../../data-model/denialQueryBucket";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";
import { throwApiError } from "../throwApiError";

/** A categorized reason recorded in an order's denial or query bucket. */
export interface DenialQueryBucketReason {
  /** Reason category, such as `clinical`. */
  category?: string;
  /** Specific reason text within the category. */
  reason_bucket: string;
}

/** Payload accepted by the denial/query bucket upsert endpoint. */
export interface DenialQueryBucketUpsertPayload {
  /** Medical PA order ID. */
  order_id: string;
  /** Regimen associated with the order. */
  regimen_name: string;
  /** Bucket being updated. */
  type: "denial" | "query";
  /** Local calendar date for the update in `YYYY-MM-DD` format. */
  date: string;
  /** Local timezone offset in minutes. */
  time_offset: number;
  /** Categorized reasons to persist in the bucket. */
  reason_bucket: DenialQueryBucketReason[];
}

/** Payload accepted by the denial/query bucket fetch endpoint. */
export interface DenialQueryBucketFetchPayload {
  /** Medical PA order ID. */
  order_id: string;
  /** Reporting date in `YYYY-MM-DD` format. */
  date: string;
  /** Local timezone offset in minutes. */
  time_offset: number;
  /** Maximum number of denial and query reasons to return. */
  limit: number;
}

/**
 * API operations for denial and query reason buckets.
 *
 * @example
 * await DenialQueryBucketService.upsertDenialQueryBucket(payload);
 */
export class DenialQueryBucketService {
  /**
   * Creates or updates an order's denial or query reason bucket.
   *
   * @param payload - Order, regimen, local time, bucket type, and categorized reasons.
   * @returns Parsed response from the denial/query bucket upsert endpoint.
   * @throws {Error} When authentication is unavailable or the API request fails.
   */
  static async upsertDenialQueryBucket(
    payload: DenialQueryBucketUpsertPayload,
  ): Promise<unknown> {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.DENIAL_QUERY_BUCKET_UPDATE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    return response.json();
  }

  /**
   * Fetches denial and query reason bucket totals for an order.
   *
   * @param payload - Order, reporting date, timezone, and result limit.
   * @param signal - Optional cancellation signal for the HTTP request.
   * @returns Aggregate bucket response containing denial and query reason entries.
   * @throws {Error} When authentication is unavailable or the API request fails.
   */
  static async getDenialQueryBucket(
    payload: DenialQueryBucketFetchPayload,
    signal?: AbortSignal,
  ): Promise<DenialQueryBucketSummaryResponse> {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.DENIAL_QUERY_BUCKET_GET, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    return response.json() as Promise<DenialQueryBucketSummaryResponse>;
  }
}
